import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError, fmtMoney, logoSrc } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Coins, TrendingUp, TrendingDown, Users, Trophy, Building2, Wallet, Activity, FileText, Hand, Layers, Check, X, Sparkles, ChevronDown, ChevronUp, Award, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#EAB308", "#22C55E", "#3B82F6", "#F43F5E", "#A855F7", "#F97316", "#14B8A6", "#EC4899"];

function PatrimonioPieTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const value = Number(p.value || 0);
  // Total from siblings: recharts includes total-of-pie in payload[0].payload but we compute defensively
  const chartData = p.payload?.__chartData__ || null;
  let total = 0;
  if (chartData) {
    total = chartData.reduce((s, d) => s + (Number(d.value) || 0), 0);
  }
  const pct = total > 0 ? (value / total) * 100 : 0;
  const color = p.color || p.payload?.fill || "#EAB308";
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-950/95 backdrop-blur px-3 py-2 shadow-2xl shadow-black/70" style={{ fontFamily: "'Space Grotesk', 'Inter', system-ui, sans-serif" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
        <span className="text-sm font-bold text-slate-100">{p.name}</span>
      </div>
      <div className="text-xl font-black font-mono text-slate-100 tabular-nums">
        {value.toLocaleString("it-IT", { maximumFractionDigits: 2 })}<span className="text-xs text-slate-500 ml-1">cr</span>
      </div>
      {total > 0 && (
        <div className="text-[11px] text-amber-300 font-mono mt-1">
          {pct.toFixed(1)}% del patrimonio totale
        </div>
      )}
    </div>
  );
}

const MANTRA_MODULE_LIST = [
  "3-4-3","3-4-1-2","3-4-2-1","3-5-2","3-5-1-1",
  "4-3-3","4-3-1-2","4-4-2","4-1-4-1","4-4-1-1","4-2-3-1",
];

export default function LeagueOverview() {
  const { league, myTeam, isAdmin, reload } = useLeague();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/leagues/${league.league_id}/dashboard`); setData(r.data); }
      catch(e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [league.league_id]);

  if (loading || !data) return <div className="text-slate-500">Caricamento dashboard…</div>;

  const teams = data.teams || [];
  const freeTeams = teams.filter(t => !t.manager_id);
  const totalBudget = teams.reduce((s, t) => s + (t.snapshot?.budget || 0), 0);
  const totalRoster = teams.reduce((s, t) => s + (t.snapshot?.valore_rosa || 0), 0);
  const totalSalaries = teams.reduce((s, t) => s + (t.snapshot?.monte_stipendi || 0), 0);
  const totalPatrimonio = teams.reduce((s, t) => s + (t.snapshot?.patrimonio || 0), 0);

  const rankChart = [...teams]
    .sort((a,b) => (b.snapshot?.patrimonio || 0) - (a.snapshot?.patrimonio || 0))
    .map(t => ({ name: t.name, Patrimonio: t.snapshot?.patrimonio || 0, Budget: t.snapshot?.budget || 0, Rosa: t.snapshot?.valore_rosa || 0 }));

  const pieDataRaw = rankChart.map(r => ({ name: r.name, value: r.Patrimonio }));
  const pieData = pieDataRaw.map(d => ({ ...d, __chartData__: pieDataRaw }));

  const claimTeam = async (team_id) => {
    try {
      await api.post(`/leagues/${league.league_id}/teams/${team_id}/claim`);
      toast.success("🎉 Squadra reclamata! Benvenuto.");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = (team_id) => {
    // Open in new tab (uses cookies for auth)
    window.open(`${API}/leagues/${league.league_id}/teams/${team_id}/bilancio.pdf`, "_blank");
  };

  const downloadLeaguePdf = () => {
    window.open(`${API}/leagues/${league.league_id}/bilancio.pdf`, "_blank");
  };

  const downloadRulebookPdf = () => {
    window.open(`${API}/leagues/${league.league_id}/regolamento.pdf`, "_blank");
  };

  const downloadSeasonReport = (team_id) => {
    window.open(`${API}/leagues/${league.league_id}/teams/${team_id}/season-report.pdf`, "_blank");
  };

  const needsToClaim = !myTeam && !isAdmin && freeTeams.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold flex items-center gap-2">
            <Activity size={14} /> Dashboard Lega
          </div>
          <h2 className="text-3xl font-black tracking-tight mt-1">🏟️ {league.name} · Panoramica</h2>
        </div>
        {myTeam && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadLeaguePdf} data-testid="league-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 🏟️ Bilancio Lega
            </button>
            <button onClick={()=>downloadPdf(myTeam.team_id)} data-testid="my-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 📄 La mia squadra
            </button>
            <button onClick={()=>downloadSeasonReport(myTeam.team_id)} data-testid="season-report-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 🎬 Report Stagione
            </button>
            <button onClick={downloadRulebookPdf} data-testid="rulebook-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 📋 Regolamento
            </button>
          </div>
        )}
        {!myTeam && isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadLeaguePdf} data-testid="league-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 🏟️ Bilancio Lega PDF
            </button>
            <button onClick={downloadRulebookPdf} data-testid="rulebook-pdf-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 font-bold uppercase text-xs">
              <FileText size={14}/> 📋 Regolamento PDF
            </button>
          </div>
        )}
      </div>

      {/* Claim banner (per manager senza team) */}
      {needsToClaim && (
        <div className="border border-emerald-500/40 rounded-lg bg-gradient-to-br from-emerald-500/10 to-transparent p-6" data-testid="claim-banner">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-2xl">🎟️</div>
            <div className="flex-1">
              <h3 className="text-xl font-black">Scegli la tua squadra!</h3>
              <p className="text-sm text-slate-400 mt-1">Sei entrato nella lega. Seleziona una delle squadre libere qui sotto per iniziare a giocare.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {freeTeams.map(t => (
              <div key={t.team_id} className="rounded-lg border border-slate-800 bg-slate-900 p-4 hover:border-emerald-500 transition-colors" data-testid={`free-team-${t.team_id}`}>
                <div className="flex items-center gap-2 mb-3">
                  {logoSrc(t.logo_url)
                    ? <img src={logoSrc(t.logo_url)} alt="" className="w-10 h-10 rounded object-cover"/>
                    : <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center text-lg">⚽</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-black truncate">{t.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">💰 {fmtMoney(t.budget)} cr</div>
                  </div>
                </div>
                <button onClick={()=>claimTeam(t.team_id)}
                  data-testid={`claim-${t.team_id}`}
                  className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs">
                  <Hand size={12}/> Questa è mia!
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard icon={<Users size={18}/>} label="Squadre" value={data.stats.team_count} color="emerald" emoji="⚽"/>
        <KpiCard icon={<Coins size={18}/>} label="Budget totale" value={fmtMoney(totalBudget)} color="amber" emoji="💰" suffix="cr"/>
        <KpiCard icon={<Trophy size={18}/>} label="Valore rose" value={fmtMoney(totalRoster)} color="blue" emoji="📊" suffix="cr"/>
        <KpiCard icon={<Wallet size={18}/>} label="Patrimonio lega" value={fmtMoney(totalPatrimonio)} color="rose" emoji="🏆" suffix="cr"/>
      </div>

      {/* Iter 64: Widget Crediti Disponibili prossima asta (admin) */}
      {isAdmin && <AvailableCreditsWidget leagueId={league.league_id} />}

      {/* My Preferred Module widget */}
      {myTeam && (
        <PreferredModuleCard leagueId={league.league_id} teamId={myTeam.team_id} teamName={myTeam.name} />
      )}

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-2xl shadow-black/40 hover:shadow-emerald-500/10 transition-shadow" data-testid="chart-standings">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold uppercase text-sm tracking-wider">📈 Classifica Patrimoniale</h3>
            <span className="text-xs text-slate-500">Budget + Valore rosa</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={rankChart}>
                <defs>
                  <linearGradient id="grBudget" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#B45309" stopOpacity={0.9}/>
                  </linearGradient>
                  <linearGradient id="grRosa" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34D399" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#047857" stopOpacity={0.9}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,.55)' }} cursor={{ fill: 'rgba(148,163,184,.08)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Budget" stackId="a" fill="url(#grBudget)" radius={[0,0,0,0]} isAnimationActive animationDuration={900}/>
                <Bar dataKey="Rosa" stackId="a" fill="url(#grRosa)" radius={[8,8,0,0]} isAnimationActive animationDuration={900}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-2xl shadow-black/40 hover:shadow-rose-500/10 transition-shadow" data-testid="chart-pie">
          <h3 className="font-bold uppercase text-sm tracking-wider mb-1">🥧 Distribuzione patrimonio</h3>
          <div className="text-[11px] text-slate-500 mb-3">
            Totale lega: <span className="text-slate-200 font-mono font-bold">{fmtMoney(pieData.reduce((s, d) => s + (d.value || 0), 0))} cr</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} innerRadius={50} outerRadius={95} paddingAngle={3} dataKey="value" isAnimationActive animationDuration={900}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<PatrimonioPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Activity Feed — solo admin */}
      <AuctionCountdown league={league} />
      {isAdmin && <ActivityFeed leagueId={league.league_id} />}

      {/* Wall of Fame — ultimi 3 trofei */}
      <WallOfFame leagueId={league.league_id} />

      {/* Team table */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden shadow-2xl shadow-black/40" data-testid="team-list">
        <div className="p-6 border-b border-slate-800">
          <h3 className="font-bold uppercase text-sm tracking-wider">🏆 Società</h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-950/50">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-3">Squadra</th>
              <th className="px-4 py-3">Manager</th>
              <th className="px-4 py-3 text-right">💰 Budget</th>
              <th className="px-4 py-3 text-right">📊 Valore rosa</th>
              <th className="px-4 py-3 text-right">🎯 P/M</th>
              <th className="px-4 py-3 text-right">💵 Stipendi</th>
              <th className="px-4 py-3 text-right">🏛️ Stadio</th>
              <th className="px-4 py-3 text-right">⭐ Ranking</th>
              <th className="px-4 py-3 text-right">📄</th>
            </tr>
          </thead>
          <tbody>
            {teams.length === 0 && (
              <tr><td colSpan="9" className="p-8 text-center text-slate-500">Nessuna squadra ancora. Vai su <strong className="text-amber-400">Admin</strong> per crearne una.</td></tr>
            )}
            {teams.map(t => {
              const s = t.snapshot || {};
              const pm = s.plus_minus || 0;
              return (
                <tr key={t.team_id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`team-row-${t.team_id}`}>
                  <td className="px-4 py-3 font-bold flex items-center gap-2">
                    {logoSrc(t.logo_url)
                      ? <img src={logoSrc(t.logo_url)} alt="" className="w-6 h-6 rounded object-cover"/>
                      : <span className="w-6 h-6 rounded bg-slate-800 flex items-center justify-center text-xs">⚽</span>}
                    {t.name}
                    {!t.manager_id && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">🎟️ Libera</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{t.manager_id ? t.manager_name : <span className="text-emerald-400 text-xs">— disponibile</span>}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(s.budget)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(s.valore_rosa)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-bold ${pm>=0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {pm >= 0 ? "↗" : "↘"} {fmtMoney(pm)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-rose-300">-{fmtMoney(s.monte_stipendi)}</td>
                  <td className="px-4 py-3 text-right"><Building2 size={12} className="inline text-amber-400"/> {s.stadium_level || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-300">{fmtMoney(s.ranking)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={()=>downloadPdf(t.team_id)}
                      data-testid={`pdf-${t.team_id}`}
                      title="Scarica bilancio PDF"
                      className="text-slate-400 hover:text-amber-400">
                      <FileText size={14}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, color, emoji, suffix }) {  const colorMap = {
    emerald: "from-emerald-500/25 to-emerald-900/5 border-emerald-500/40 text-emerald-400 shadow-emerald-500/10 hover:shadow-emerald-500/30",
    amber: "from-amber-500/25 to-amber-900/5 border-amber-500/40 text-amber-400 shadow-amber-500/10 hover:shadow-amber-500/30",
    blue: "from-blue-500/25 to-blue-900/5 border-blue-500/40 text-blue-400 shadow-blue-500/10 hover:shadow-blue-500/30",
    rose: "from-rose-500/25 to-rose-900/5 border-rose-500/40 text-rose-400 shadow-rose-500/10 hover:shadow-rose-500/30",
  }[color] || "";
  return (
    <div className={`rounded-3xl border bg-gradient-to-br ${colorMap} p-5 shadow-xl hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm`} data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl drop-shadow-md">{emoji}</span>
        <span className="opacity-70">{icon}</span>
      </div>
      <div className="text-[10px] tracking-widest text-slate-400 uppercase font-bold">{label}</div>
      <div className="text-2xl font-mono font-black mt-1 text-white">{value}{suffix && <span className="text-sm text-slate-500 ml-1">{suffix}</span>}</div>
    </div>
  );
}


function ActivityFeed({ leagueId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${leagueId}/activity-feed`, { params: { limit: 15 } });
        setEvents(r.data.events || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [leagueId]);

  const fmtWhen = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}g fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
  };

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid="activity-feed">
      <div className="p-5 border-b border-slate-800 flex items-center justify-between">
        <h3 className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          📰 Attività recente
        </h3>
        <span className="text-[10px] text-slate-500 uppercase tracking-widest">{events.length} eventi</span>
      </div>
      {loading ? (
        <div className="p-8 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : events.length === 0 ? (
        <div className="p-10 text-center text-slate-500 text-sm">
          <div className="text-3xl mb-2">📭</div>
          Nessun evento ancora — appena farete trasferimenti, rinnovi o chiusure di stagione, apparirà tutto qui.
        </div>
      ) : (
        <ol className="divide-y divide-slate-800 max-h-[440px] overflow-y-auto">
          {events.map((e, i) => (
            <li key={i} className="p-4 flex items-start gap-3 hover:bg-slate-800/30 transition-colors" data-testid={`activity-item-${i}`}>
              <div className="w-10 h-10 rounded-2xl bg-slate-950/70 border border-slate-800 flex items-center justify-center text-xl shrink-0 shadow-inner">
                {e.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-bold text-sm">
                    {e.title}
                    {e.player_name && e.kind === "operation" && (
                      <span className="text-slate-300 font-semibold ml-1">· {e.player_name}</span>
                    )}
                    {e.player_role && (
                      <span className="ml-2 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400">{e.player_role}</span>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-500 whitespace-nowrap">{fmtWhen(e.at)}</span>
                </div>
                {(e.from_team || e.to_team) && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {e.from_team && <span className="text-rose-300">⚽ {e.from_team}</span>}
                    {e.from_team && e.to_team && <span className="mx-2 text-slate-600">→</span>}
                    {e.to_team && <span className="text-emerald-300">⚽ {e.to_team}</span>}
                    {!e.to_team && e.kind === "operation" && !e.from_team && <span className="text-slate-500">—</span>}
                  </div>
                )}
                {e.amount > 0 && (
                  <div className="text-xs mt-0.5">
                    <span className="font-mono font-black text-amber-300">{e.amount}</span>
                    <span className="text-slate-500 text-[10px] ml-1 uppercase tracking-widest">cr</span>
                  </div>
                )}
                {e.note && !(e.from_team || e.to_team) && (
                  <div className="text-xs text-slate-400 mt-0.5">{e.note}</div>
                )}
                {e.by && (
                  <div className="text-[10px] text-slate-600 mt-0.5">👤 {e.by}</div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function AuctionCountdown({ league }) {
  const [now, setNow] = useState(Date.now());
  const deadline = league.settings?.auction_deadline;

  useEffect(() => {
    if (!deadline) return;
    const t = setInterval(() => setNow(Date.now()), 60000);
    // Trigger reminder check on load
    api.post(`/leagues/${league.league_id}/check-auction-reminder`).catch(() => {});
    return () => clearInterval(t);
  }, [deadline, league.league_id]);

  if (!deadline) return null;
  let target;
  try { target = new Date(deadline).getTime(); }
  catch { return null; }
  const diff = target - now;
  const past = diff <= 0;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);

  const bgClass = past ? "from-slate-800 to-slate-950 border-slate-700"
    : diff < 24 * 3600000 ? "from-rose-500/20 to-slate-950 border-rose-500/50 shadow-rose-500/20 animate-pulse-slow"
    : diff < 72 * 3600000 ? "from-amber-500/20 to-slate-950 border-amber-500/50 shadow-amber-500/20"
    : "from-emerald-500/10 to-slate-950 border-emerald-500/30";

  return (
    <div className={`border rounded-3xl bg-gradient-to-br ${bgClass} p-5 shadow-2xl`} data-testid="auction-countdown">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="text-4xl">⏰</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Asta furto di luglio</div>
            <div className="text-lg font-black">
              {past ? "🏁 Asta conclusa" : `${d > 0 ? d + "g " : ""}${h}h ${m}m alla scadenza`}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              📅 {new Date(deadline).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>
        {!past && (
          <div className="flex gap-2 text-center">
            <TimeBox v={d} label="giorni"/>
            <TimeBox v={h} label="ore"/>
            <TimeBox v={m} label="min"/>
          </div>
        )}
      </div>
    </div>
  );
}

function TimeBox({ v, label }) {
  return (
    <div className="rounded-2xl bg-slate-950/70 border border-slate-800 px-4 py-2 min-w-[60px]">
      <div className="text-2xl font-mono font-black text-amber-300">{String(v).padStart(2, "0")}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function PreferredModuleCard({ leagueId, teamId, teamName }) {
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [best, setBest] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  const loadAnalysis = async () => {
    try {
      const r = await api.get(`/leagues/${leagueId}/teams/${teamId}/preferred-module-analysis`);
      setAnalysis(r.data);
    } catch (e) {
      // Silent fail (potrebbe non essere ancora impostato)
      setAnalysis({ preferred_module: null });
    } finally {
      setLoading(false);
    }
  };

  const loadBest = async () => {
    setSuggestLoading(true);
    try {
      const r = await api.get(`/leagues/${leagueId}/teams/${teamId}/best-modules`);
      setBest(r.data?.best_modules || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSuggestLoading(false);
    }
  };

  useEffect(() => {
    loadAnalysis();
  }, [leagueId, teamId]);

  const setModule = async (module) => {
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/teams/${teamId}/preferred-module`, { module: module || null });
      await loadAnalysis();
      toast.success(module ? `⚙️ Modulo preferito: ${module}` : "🗑️ Modulo rimosso");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSuggest = async () => {
    const next = !suggestOpen;
    setSuggestOpen(next);
    if (next && best.length === 0) await loadBest();
  };

  const mod = analysis?.preferred_module;
  const total = analysis?.total_slots || 0;
  const covered = analysis?.covered_slots || 0;
  const score = Math.round(analysis?.score || 0);
  const sofferenze = analysis?.sofferenze || [];
  const slots = analysis?.slots || [];

  const scoreColor = score >= 90 ? "text-emerald-400"
    : score >= 70 ? "text-amber-300"
    : score >= 50 ? "text-orange-400"
    : "text-rose-400";
  const barColor = score >= 90 ? "bg-emerald-400"
    : score >= 70 ? "bg-amber-400"
    : score >= 50 ? "bg-orange-400"
    : "bg-rose-500";

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-indigo-900/30 via-slate-900 to-slate-950 p-5 sm:p-6 shadow-2xl shadow-black/40" data-testid="preferred-module-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-2xl shadow-inner">
            🎯
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-black flex items-center gap-1">
              <Layers size={11}/> Modulo preferito
            </div>
            <h3 className="text-lg font-black tracking-tight">{teamName}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="preferred-module-select"
            value={mod || ""}
            disabled={saving || loading}
            onChange={(e)=>setModule(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-indigo-500"
          >
            <option value="">— Nessuno —</option>
            {MANTRA_MODULE_LIST.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {mod && (
            <button
              data-testid="preferred-module-clear"
              onClick={()=>setModule(null)}
              disabled={saving}
              title="Rimuovi modulo preferito"
              className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-rose-400 hover:border-rose-500/50 transition-colors"
            >
              <X size={14}/>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-4">Caricamento…</div>
      ) : !mod ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-700 rounded-2xl p-4 text-center" data-testid="preferred-module-empty">
          💡 Seleziona un modulo Mantra qui sopra per vedere subito la <strong className="text-indigo-300">% di copertura</strong> della tua rosa.
        </div>
      ) : (
        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          {/* SIDE: Circular coverage ring */}
          <div className="rounded-2xl bg-slate-950/70 border border-slate-800 p-4 flex flex-col items-center justify-center relative overflow-hidden">
            {/* Subtle grid background */}
            <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #fff 1px, transparent 1px)', backgroundSize: '14px 14px' }}></div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3 relative z-10">Copertura rosa</div>
            {/* SVG Ring */}
            <div className="relative w-32 h-32 z-10">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(30 41 59)" strokeWidth="8"/>
                <circle
                  cx="50" cy="50" r="42" fill="none"
                  stroke={score >= 90 ? "rgb(16 185 129)" : score >= 70 ? "rgb(251 191 36)" : score >= 50 ? "rgb(251 146 60)" : "rgb(244 63 94)"}
                  strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(score/100)*264} 264`}
                  className="transition-all duration-1000 ease-out"
                  style={{ filter: `drop-shadow(0 0 8px ${score >= 90 ? 'rgba(16,185,129,0.6)' : score >= 70 ? 'rgba(251,191,36,0.6)' : score >= 50 ? 'rgba(251,146,60,0.6)' : 'rgba(244,63,94,0.6)'})` }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className={`text-3xl font-black font-mono ${scoreColor}`} data-testid="preferred-module-score">{score}%</div>
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-bold mt-0.5">{covered}/{total} slot</div>
              </div>
            </div>
            {sofferenze.length > 0 && (
              <div className="mt-3 text-[10px] px-2 py-1 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 font-bold relative z-10">
                ⚠️ {sofferenze.length} sofferenz{sofferenze.length === 1 ? "a" : "e"}
              </div>
            )}
          </div>

          {/* MAIN: Football pitch layout */}
          <div className="rounded-2xl border border-emerald-900/40 p-4 relative overflow-hidden"
               style={{
                 background: "linear-gradient(180deg, rgba(6,78,59,0.25) 0%, rgba(2,44,34,0.35) 100%)"
               }}>
            {/* Pitch stripes background */}
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                 style={{ backgroundImage: 'repeating-linear-gradient(180deg, transparent 0px, transparent 24px, rgba(255,255,255,0.5) 24px, rgba(255,255,255,0.5) 25px)' }}></div>
            {/* Center line */}
            <div className="absolute left-4 right-4 top-1/2 h-px bg-white/[0.08] pointer-events-none"></div>
            {/* Center circle */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-white/[0.06] pointer-events-none"></div>

            <div className="flex items-center justify-between mb-3 relative">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1.5">
                <Layers size={11}/> Formazione tipo · {mod}
              </div>
            </div>

            {/* Pitch rows: A (top), M, D, GK (bottom) */}
            <div className="flex flex-col gap-2 relative">
              {["A", "M", "D", "GK"].map((line, lineIdx) => {
                const rowSlots = slots.filter(s => s.line === line);
                if (rowSlots.length === 0) return null;
                const lineMeta = {
                  A:  { name: "Attacco",     dot: "#f43f5e",
                        slotCls: "bg-rose-500/20 border-rose-500/50 text-rose-200 shadow-rose-500/10" },
                  M:  { name: "Centrocampo", dot: "#10b981",
                        slotCls: "bg-emerald-500/20 border-emerald-500/50 text-emerald-200 shadow-emerald-500/10" },
                  D:  { name: "Difesa",      dot: "#38bdf8",
                        slotCls: "bg-sky-500/20 border-sky-500/50 text-sky-200 shadow-sky-500/10" },
                  GK: { name: "Portiere",    dot: "#fbbf24",
                        slotCls: "bg-amber-500/20 border-amber-500/50 text-amber-200 shadow-amber-500/10" },
                }[line];
                return (
                  <div key={line} className="flex items-center gap-3">
                    {/* Line label + dot */}
                    <div className="w-14 shrink-0 flex flex-col items-start">
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: lineMeta.dot, boxShadow: `0 0 6px ${lineMeta.dot}` }}></span>
                        <span className="text-[9px] font-mono uppercase tracking-widest font-bold" style={{ color: lineMeta.dot }}>{lineMeta.name}</span>
                      </div>
                    </div>
                    {/* Slots for this line */}
                    <div className="flex-1 flex flex-wrap gap-1.5 justify-center">
                      {rowSlots.map((s, i) => {
                        const isCovered = (s.candidates_count || 0) > 0;
                        const covName = isCovered ? (s.top_candidates?.[0]?.name || "") : "scoperto";
                        return (
                          <span key={`${s.slot_id}-${i}`}
                            data-testid={`slot-${s.slot_id}`}
                            title={`${s.label}: ${covName}`}
                            className={`text-[11px] font-black px-2.5 py-1.5 rounded-lg border shadow-md transition-all duration-300 hover:scale-110 hover:-translate-y-0.5 cursor-help ${
                              isCovered
                                ? lineMeta.slotCls
                                : "bg-rose-950/60 border-rose-500/60 text-rose-300 shadow-rose-500/20 animate-pulse"
                            }`}
                            style={isCovered ? { animation: `slotIn 400ms ${lineIdx * 100 + i * 60}ms both ease-out` } : {}}
                          >
                            {isCovered ? "✓" : "✗"} {s.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <style>{`@keyframes slotIn { from { opacity: 0; transform: translateY(8px) scale(0.9); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
          </div>
        </div>
      )}

      {/* AI Suggested modules */}
      <div className="mt-4 pt-4 border-t border-slate-800/70">
        <button
          data-testid="suggest-modules-toggle"
          onClick={toggleSuggest}
          className="w-full flex items-center justify-between px-3 py-2 rounded-2xl bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 hover:border-indigo-400 hover:from-indigo-600/30 hover:to-purple-600/30 transition-all"
        >
          <span className="flex items-center gap-2 text-sm font-black text-indigo-200">
            <Sparkles size={14} className="text-amber-300"/> Suggeriti dall&apos;AI Coach
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold ml-1">
              (top 3 moduli per la tua rosa)
            </span>
          </span>
          {suggestOpen ? <ChevronUp size={16} className="text-indigo-300"/> : <ChevronDown size={16} className="text-indigo-300"/>}
        </button>

        {suggestOpen && (
          <div className="mt-3" data-testid="suggested-modules-panel">
            {suggestLoading ? (
              <div className="text-slate-500 text-sm py-4 text-center">Analizzando la rosa…</div>
            ) : best.length === 0 ? (
              <div className="text-slate-500 text-sm py-4 text-center">Nessun suggerimento disponibile.</div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                {best.map((b, i) => {
                  const bs = Math.round(b.score || 0);
                  const isCurrent = b.module === mod;
                  const rank = i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉";
                  const rankColor = i === 0 ? "border-amber-400/60 shadow-amber-400/20"
                    : i === 1 ? "border-slate-400/50 shadow-slate-400/10"
                    : "border-orange-500/40 shadow-orange-500/10";
                  return (
                    <div key={b.module}
                      className={`rounded-2xl border ${rankColor} bg-slate-950/70 p-3 shadow-lg hover:-translate-y-0.5 transition-transform`}
                      data-testid={`suggested-module-${b.module}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xl">{rank}</span>
                          <span className="font-black text-white">{b.module}</span>
                        </div>
                        <span className={`text-lg font-mono font-black ${
                          bs >= 90 ? "text-emerald-400"
                          : bs >= 70 ? "text-amber-300"
                          : "text-orange-400"
                        }`}>{bs}%</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mb-2 font-mono">
                        {b.covered}/{b.total} slot coperti
                      </div>
                      <button
                        data-testid={`apply-suggested-${b.module}`}
                        disabled={saving || isCurrent}
                        onClick={()=>setModule(b.module)}
                        className={`w-full text-[11px] font-black uppercase tracking-wider px-2 py-1.5 rounded-lg transition-colors ${
                          isCurrent
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 cursor-default"
                            : "bg-indigo-500 hover:bg-indigo-400 text-white shadow-md shadow-indigo-500/30"
                        }`}
                      >
                        {isCurrent ? "✓ Attivo" : "✨ Imposta"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}




/* -------------------- Wall of Fame: ultimi 3 trofei -------------------- */
function WallOfFame({ leagueId }) {
  const { teams } = useLeague();
  const [trophies, setTrophies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${leagueId}/history`);
        // Estrai tutti i trofei (position=1), ordinati per stagione decrescente, top 3
        const all = [];
        for (const s of (r.data || [])) {
          for (const c of (s.competitions || [])) {
            const winner = (c.positions || []).find(p => p.position === 1);
            if (!winner) continue;
            all.push({
              season: s.season,
              closed_at: s.closed_at,
              competition_id: c.competition_id,
              competition_name: c.name,
              emoji: c.emoji || "🏆",
              title: winner.trophy || c.title_awarded || c.name,
              team_id: winner.team_id,
              team_name: winner.team_name,
              prize: winner.prize || 0,
              photo_url: c.winner_photo_url || null,
            });
          }
        }
        all.sort((a, b) => b.season - a.season);
        setTrophies(all.slice(0, 3));
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [leagueId]);

  const teamLogoOf = (tid) => teams.find(t => t.team_id === tid)?.logo_url;

  if (loading || trophies.length === 0) return null;

  return (
    <div className="border border-amber-500/30 rounded-3xl bg-gradient-to-br from-amber-900/25 via-slate-900 to-slate-950 shadow-2xl shadow-black/40 overflow-hidden" data-testid="wall-of-fame">
      <div className="p-5 border-b border-amber-500/20 flex items-center justify-between flex-wrap gap-2 bg-gradient-to-r from-amber-500/10 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-2xl shadow-inner">🏆</div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black flex items-center gap-1">
              <Award size={11}/> Wall of Fame
            </div>
            <h3 className="text-lg font-black tracking-tight">Ultimi trofei della lega</h3>
          </div>
        </div>
        <Link
          to="../trofei"
          data-testid="wall-of-fame-see-all"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 font-black text-xs uppercase tracking-wider"
        >
          Albo d&apos;Oro <ChevronRight size={12}/>
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-3 p-5">
        {trophies.map((t, i) => (
          <TrophyMiniCard key={`${t.competition_id}-${t.season}`} t={t} rank={i} teamLogo={teamLogoOf(t.team_id)}/>
        ))}
      </div>
    </div>
  );
}

function TrophyMiniCard({ t, rank, teamLogo }) {
  const glow = rank === 0
    ? "border-amber-400/60 shadow-amber-400/20 -translate-y-0.5"
    : rank === 1
    ? "border-amber-500/40 shadow-black/20"
    : "border-slate-800 shadow-black/10";
  return (
    <div className={`relative rounded-2xl border ${glow} bg-gradient-to-br from-slate-950 to-slate-900 shadow-lg overflow-hidden hover:-translate-y-1 transition-transform`}
      data-testid={`trophy-card-${t.competition_id}-${t.season}`}>
      {/* Photo hero (se presente) */}
      {t.photo_url && (
        <div className="relative h-24 w-full bg-slate-950 overflow-hidden">
          <img src={logoSrc(t.photo_url)} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"/>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black flex items-center gap-1">
            <span className="text-base">{t.emoji}</span> {t.competition_name}
          </div>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 font-black">S{t.season}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            {logoSrc(teamLogo)
              ? <img src={logoSrc(teamLogo)} alt="" className="w-12 h-12 rounded-xl object-cover border-2 border-amber-400 shadow-lg shadow-amber-500/20"/>
              : <div className="w-12 h-12 rounded-xl bg-slate-800 border-2 border-amber-400 flex items-center justify-center text-xl shadow-lg shadow-amber-500/20">⚽</div>}
            <div className="absolute -bottom-1 -right-1 text-base">🏆</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-black">Campione</div>
            <div className="font-black text-white text-sm truncate" data-testid={`trophy-winner-${t.competition_id}-${t.season}`}>{t.team_name}</div>
            <div className="text-xs text-amber-300 font-mono truncate">{t.title}</div>
            {t.prize > 0 && (
              <div className="text-[10px] text-emerald-300 font-mono">+{fmtMoney(Math.round(t.prize))} cr</div>
            )}
          </div>
        </div>
      </div>
      {rank === 0 && (
        <div className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400 text-slate-950 font-black uppercase tracking-widest shadow-lg">
          🆕 Ultimo
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// ITER 64: WIDGET CREDITI DISPONIBILI PROSSIMA ASTA
// ═══════════════════════════════════════════════════════════════════

function AvailableCreditsWidget({ leagueId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState("riparazione");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/leagues/${leagueId}/available-credits?context=${context}`);
        setData(r.data);
      } catch (e) { /* silenzioso: widget optional */ }
      finally { setLoading(false); }
    })();
  }, [leagueId, context]);

  if (loading || !data || !data.teams?.length) return null;

  const maxAvail = Math.max(...data.teams.map(t => t.credits_available), 1);

  return (
    <div className="border border-amber-500/30 rounded-3xl bg-gradient-to-br from-amber-950/30 to-slate-950 p-5 shadow-2xl shadow-black/40" data-testid="widget-available-credits">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/20 border border-amber-500/50 flex items-center justify-center text-2xl">💰</div>
          <div>
            <div className="text-xs tracking-[0.3em] text-amber-300 uppercase font-bold">Crediti prossima asta</div>
            <h3 className="text-lg font-black">Chi può spendere quanto</h3>
          </div>
        </div>
        <div className="flex rounded-2xl border border-amber-500/30 overflow-hidden">
          <button onClick={() => setContext("riparazione")} data-testid="credits-ctx-riparazione"
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${context === "riparazione" ? "bg-amber-500/25 text-amber-200" : "text-slate-500 hover:text-amber-300"}`}>
            Riparazione
          </button>
          <button onClick={() => setContext("furti")} data-testid="credits-ctx-furti"
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${context === "furti" ? "bg-amber-500/25 text-amber-200" : "text-slate-500 hover:text-amber-300"}`}>
            Furti
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {data.teams.map((t, i) => {
          const pct = (t.credits_available / maxAvail) * 100;
          const isTop = i === 0;
          return (
            <div key={t.team_id} data-testid={`credits-row-${t.team_id}`}>
              <div className="flex items-center justify-between gap-2 text-xs mb-0.5 flex-wrap">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <span className="w-5 h-5 rounded-full bg-slate-800 text-slate-400 font-mono font-bold flex items-center justify-center text-[10px] shrink-0">{i + 1}</span>
                  <span className={`font-bold truncate ${isTop ? "text-amber-300" : "text-slate-200"}`}>{t.team_name}</span>
                  {isTop && <span className="text-[9px] text-amber-400 shrink-0">👑</span>}
                </div>
                <div className="flex items-center gap-2 font-mono shrink-0">
                  <span className="text-slate-500 text-[10px] sm:text-xs hidden sm:inline">budget {fmtMoney(t.budget)}cr · − stip {fmtMoney(t.monte_stipendi)}cr</span>
                  <span className="text-slate-500 text-[10px] sm:hidden">{fmtMoney(t.budget)}·{fmtMoney(t.monte_stipendi)}</span>
                  <span className={`font-black text-base sm:text-lg whitespace-nowrap ${isTop ? "text-amber-300" : "text-emerald-400"}`}>{fmtMoney(t.credits_available)}<span className="text-[10px] ml-0.5">cr</span></span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-800/60 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${isTop ? "bg-gradient-to-r from-amber-500 to-yellow-400" : "bg-emerald-500/60"}`} style={{ width: `${pct}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[10px] text-slate-500 border-t border-slate-800 pt-2">
        💡 Formula: budget − monte_stipendi. Context <strong className="text-amber-300">{context}</strong>. Aggiornato in real-time dalle rose attuali.
      </div>
    </div>
  );
}
